import React, {useContext, useState} from "react";
import {Box, FormControl, LinearProgress, Link, Stack, TextField, Tooltip, Typography} from "@mui/material";
import {PresalesIssue} from "arteco-api-client-ts";
import {FormContext} from "../../../../components/form/form_inputs/form_buttons";
import {fieldMarginBottom} from "../../../../components/form/form_constants";
import {euros, SoftChip, Tone} from "../../../../components/kit";

/** Preventa (#14761, #14762): piezas que comparten la pantalla de Preventa y la ficha de la oportunidad. */

export const REDMINE_ISSUE = "https://redmine.artecoapps.com/issues/";

export const BALL: Record<string, { label: string, tone: Tone }> = {
	OURS: {label: "Nuestra", tone: "warning"},
	THEIRS: {label: "Suya", tone: "info"},
	NOBODY: {label: "Nadie la vigila", tone: "error"},
};

/** Importe de la oportunidad: sin importe o 0 es «—», que una oportunidad sin cifrar no vale 0 €. */
export const amountOrDash = (n?: number | null) => n ? euros(n) : "—";

export const hours = (n?: number | null) => n === undefined || n === null ? "—"
	: n.toLocaleString("es-ES", {maximumFractionDigits: 2});

const issueTone = (i: PresalesIssue, today: string): Tone => {
	if (!i.status) {
		return "error";
	}
	if (i.closed) {
		return "neutral";
	}
	return i.startDate && i.startDate > today ? "neutral" : "info";
};

const issueTitle = (i: PresalesIssue, today: string) => {
	if (!i.status) {
		return "No se encuentra en Redmine";
	}
	const postponed = !i.closed && i.startDate && i.startDate > today ? ` · aplazada al ${i.startDate.split("-").reverse().join("/")}` : "";
	return `${i.subject ?? ""} · ${i.status}${postponed} · ${hours(i.spentHours)} h`;
};

/** Las issues enlazadas como etiquetas que abren Redmine; el color dice si están vivas. */
export const IssueChips = ({ids, issues}: { ids?: number[], issues?: PresalesIssue[] }) => {
	const today = new Date().toISOString().slice(0, 10);
	const byId = new Map((issues ?? []).map(i => [i.id, i]));
	if (!ids || ids.length === 0) {
		return <Typography variant="body2" color="text.disabled">Sin issues</Typography>;
	}
	return (
		<Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
			{ids.map(id => {
				const i = byId.get(id);
				return (
					<Tooltip key={id} title={i ? issueTitle(i, today) : "Aún no leída de Redmine"}>
						<Link href={REDMINE_ISSUE + id} target="_blank" rel="noopener" underline="none"
							  onClick={e => e.stopPropagation()}>
							<SoftChip label={"#" + id} tone={i ? issueTone(i, today) : "neutral"}/>
						</Link>
					</Tooltip>
				);
			})}
		</Stack>
	);
};

/** «1,5 de 2 h» con su barra; roja al llegar al tope. Con `own`, avisa de que el tope es propio y no el de Parámetros. */
export const HoursMeter = ({spent, cap, own = false, width = 120}: { spent?: number | null, cap?: number | null, own?: boolean, width?: number | string }) => {
	const reached = spent != null && cap != null && cap > 0 && spent >= cap;
	const pct = spent != null && cap ? Math.min(100, spent * 100 / cap) : 0;
	return (
		<Box sx={{width}}>
			<Typography variant="body2" fontWeight={600} color={reached ? "error.main" : undefined}
						sx={{fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap"}}>
				{hours(spent ?? 0)} de {hours(cap)} h
				{own && <Typography component="span" variant="caption" color="text.secondary" fontWeight={400}> · tope propio</Typography>}
			</Typography>
			<LinearProgress variant="determinate" value={pct} color={reached ? "error" : pct >= 75 ? "warning" : "primary"}
							sx={{mt: 0.5, height: 5, borderRadius: 3, "& .MuiLinearProgress-bar": {borderRadius: 3}}}/>
		</Box>
	);
};

/** «2 h, por defecto» o «6 h, propio»: de dónde sale el tope de una oportunidad (#14767). */
export const capSourceText = (effective?: number | null, own?: number | null) =>
	own != null ? hours(own) + " h, propio" : effective != null ? hours(effective) + " h, por defecto" : "por defecto";

/**
 * Tope de horas propio de la oportunidad (#14767). Vacío hereda el de Configuración › Parámetros
 * (presales.hours-cap): manda 0, que el API distingue de «no tocar» (nulo) y guarda como sin tope propio.
 */
export const HoursCapInput = ({field, label, effective}: { field: string, label: string, effective?: number | null }) => {
	const form = useContext(FormContext).form;
	const initial = form.field(field).value() as number | null | undefined;
	const [text, setText] = useState<string>(initial == null ? "" : String(initial));
	const parse = (t: string) => {
		const n = parseFloat(t.replace(",", "."));
		return t.trim() === "" || isNaN(n) ? 0 : n;
	};

	form.register(field, () => parse(text), v => setText(v == null ? "" : String(v)));

	const own = text.trim() !== "" && parse(text) > 0;
	return (
		<Box className={["form-input", "form-input-" + field].join(" ")} mb={fieldMarginBottom}>
			<FormControl fullWidth>
				<TextField label={label} value={text} variant="filled" inputMode="decimal"
						   placeholder={own || effective == null ? undefined : hours(effective)}
						   helperText={own ? "Propio. Vacío usa el de Parámetros."
							   : "Vacío: el de Parámetros" + (effective != null ? " (" + hours(effective) + " h)." : ".")}
						   onChange={e => setText(e.target.value)}/>
			</FormControl>
		</Box>
	);
};

/**
 * Números de issue separados por comas, para la ficha de la oportunidad. Vacío las desenlaza (manda
 * una lista vacía, que el API distingue de «no tocar»).
 */
export const IssueIdsInput = ({field, label}: { field: string, label: string }) => {
	const form = useContext(FormContext).form;
	const initial = (form.field(field).value() as number[] | undefined) ?? [];
	const [text, setText] = useState<string>(initial.join(", "));
	const parse = (t: string) => [...new Set(t.split(/[\s,;#]+/).map(s => parseInt(s, 10)).filter(n => n > 0))];

	form.register(field, () => parse(text), v => setText(((v as number[] | undefined) ?? []).join(", ")));

	return (
		<Box className={["form-input", "form-input-" + field].join(" ")} mb={fieldMarginBottom}>
			<FormControl fullWidth>
				<TextField label={label} value={text} placeholder="14761, 14762" variant="filled"
						   helperText="Números de issue de Redmine, separados por comas."
						   onChange={e => setText(e.target.value)}/>
			</FormControl>
		</Box>
	);
};
